import { Component, EventEmitter, inject, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { Observable, shareReplay } from 'rxjs';
import { SortingOrderType } from 'src/app/interfaces/search.interface';
import { TableCellOnActionEvent, UserTableConstructorInfo } from 'src/app/interfaces/tables.interfaces';
import { MediaQueryService } from 'src/app/services/media-query-service';
import { AccountStatus } from 'src/app/swagger-api';
import AppValues from '../../common/app.values';
import { DropdownFilter, FilterOption } from '../filter-drop-down/filter-drop-down.component';
import { SortCategories, SortOptions } from '../sort-elements/sort-drop-down/sort-drop-down.helpers';
import {
  setSortOptionsForIconElement,
  SortIconItemOptions,
} from '../sort-elements/sort-icon-item/sort-icon-item.helpers';
import { CellType, ColumnTypes } from './grid-table.enum';

@Component({
  selector: 'app-grid-users-list',
  templateUrl: './grid-users-list.component.html',
  styleUrls: ['./grid-users-list.component.scss'],
})
export class GridUsersListComponent<T> implements OnChanges {
  @Input({ required: true }) public constructorData: UserTableConstructorInfo;

  @Input({ required: false }) public noUsersHint: string;
  @Input({ required: false }) public ascending: boolean;
  @Input({ required: false }) public currentSortCategory: SortingOrderType;
  @Input({ required: false }) public isLoading = false;
  @Input({ required: false }) public sortOptions: SortOptions[];
  @Input({ required: false }) public filter: DropdownFilter<T>[];
  @Input({ required: false }) public currentFilterCategory: FilterOption<T>;

  // Pagination controls
  @Input({ required: false }) public currentPage = 1;
  @Input({ required: false }) public totalCount = 0;
  @Input({ required: false }) public pagination = false;
  @Input({ required: false }) public pageSize = AppValues.PAGE_LIMIT;

  @Output() public action: EventEmitter<TableCellOnActionEvent> = new EventEmitter<TableCellOnActionEvent>();
  @Output() public toggleSorting: EventEmitter<SortingOrderType> = new EventEmitter<SortingOrderType>();
  @Output() public toggleSortingByType: EventEmitter<SortOptions> = new EventEmitter<SortOptions>();

  @Output() public toggleFilteringByType: EventEmitter<FilterOption<T>> = new EventEmitter<FilterOption<T>>();

  @Output() public pageChange: EventEmitter<number> = new EventEmitter<number>();

  public onPageChange(page: number): void {
    this.pageChange.emit(page);
  }

  public columns: typeof ColumnTypes = ColumnTypes;
  public cellAction: typeof CellType = CellType;
  public sortCategories: typeof SortingOrderType = SortingOrderType;

  public dateSortIconParams: SortIconItemOptions;
  public nameSortIconParams: SortIconItemOptions;

  public readonly ACTIVE_ACCOUNT_STATUS: string = AccountStatus.ACTIVE;
  public readonly DATE_TIME_FORMAT_SHORT: string = AppValues.DATE_TIME_FORMAT_SHORT;
  public readonly NOT_SPECIFIED_HINT: string = 'DISEASES_SPECIALTIES_LIST_NOT_SPECIFIED_HINT';

  public readonly isMobile$: Observable<boolean> = inject(MediaQueryService).match$.pipe(shareReplay(1));

  public get header(): ColumnTypes[] {
    return this.constructorData.header;
  }

  public get allowedNameSorting(): boolean {
    return this.sortOptions.some((options: SortOptions) => options.sortCategory === SortCategories.NAME_ASC);
  }

  public get allowedDateSorting(): boolean {
    return this.sortOptions.some((options: SortOptions) => options.sortCategory === SortCategories.DATE_ASC);
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['ascending'] || changes['currentSortCategory']) {
      this.nameSortIconParams = setSortOptionsForIconElement({
        ascending: this.ascending,
        currentSortCategory: this.currentSortCategory,
        preparedSortCategory: this.sortCategories.NAME,
      });
      this.dateSortIconParams = setSortOptionsForIconElement({
        ascending: this.ascending,
        currentSortCategory: this.currentSortCategory,
        preparedSortCategory: this.sortCategories.DATE,
      });
    }
  }

  public onAction(event: TableCellOnActionEvent): void {
    this.action.emit(event);
  }

  public toggleSortingOrder(sortCategory: SortingOrderType): void {
    this.toggleSorting.emit(sortCategory);
  }

  public toggleSortingOrderByType(sortOptions: SortOptions): void {
    this.toggleSortingByType.emit(sortOptions);
  }

  public applyFilterByCategory(filterOptions: FilterOption<T>): void {
    this.toggleFilteringByType.emit(filterOptions);
  }
}
